import { createContext, useContext, useEffect, useState } from 'react';

/**
 * Auth Context for user authentication
 */
const AuthContext = createContext(null);
const AUTH_STORAGE_KEY = 'adaptive-lms-auth-user';

function loadStoredUser() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persistUser(userData) {
  if (typeof window === 'undefined') {
    return;
  }

  if (!userData) {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = loadStoredUser();
    setUser(storedUser);
    setLoading(false);
  }, []);

  const login = (userData) => {
    setUser(userData);
    persistUser(userData);
  };

  const logout = () => {
    setUser(null);
    persistUser(null);
  };

  const updateUser = (updates) => {
    setUser(prevUser => {
      if (!prevUser) {
        return prevUser;
      }

      const updatedUser = { ...prevUser, ...updates };
      persistUser(updatedUser);
      return updatedUser;
    });
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
